/**
 * Language-pack registry and language detection (ENGINE-SPEC §2.12, DESIGN ED-6).
 *
 * Detection: script census (Latin, Hangul, Han, Cyrillic, …) then the function-word share of each installed
 * pack. The document language is the best-fitting installed pack whose fit is ≥ 0.25, otherwise 'und'.
 */
import type { ChannelId } from '../channels/types.ts';
import englishPack, { AGNOSTIC_CHANNELS } from './en/index.ts';
import { scriptCensus } from './script.ts';
import type { ChannelSupport, LanguageDetection, LanguagePack } from './types.ts';

export type * from './types.ts';
export { AGNOSTIC_CHANNELS, scriptCensus };

/** Minimum pack fit to accept a language (§2.12). */
export const DETECT_MIN_FIT = 0.25;
/** English-native channels abstain when the prose's English function-word share is below this (C-LANG). */
export const EN_GATE = 0.25;

const packs = new Map<string, LanguagePack>();

export function registerPack(pack: LanguagePack): void {
	packs.set(pack.code, pack);
}

export function getPack(code: string): LanguagePack | undefined {
	return packs.get(code);
}

export function listPacks(): LanguagePack[] {
	return [...packs.values()];
}

registerPack(englishPack);

/** The pack used to tokenise text whose language has no pack of its own (the language-independent rules). */
export const DEFAULT_PACK_CODE = 'en';

function sampleOf(text: string): string {
	const n = text.length;
	if (n <= 30000) return text;
	const mid = Math.floor(n / 2) - 5000;
	return `${text.slice(0, 10000)}\n${text.slice(mid, mid + 10000)}\n${text.slice(n - 10000)}`;
}

export function detectLanguage(text: string): LanguageDetection {
	const sample = sampleOf(text);
	const census = scriptCensus(sample);
	const fits: Record<string, number> = {};
	let bestCode = 'und';
	let bestFit = 0;
	for (const pack of packs.values()) {
		const fit = census.letters === 0 ? 0 : pack.detect(sample);
		fits[pack.code] = fit;
		if (fit > bestFit) {
			bestFit = fit;
			bestCode = pack.code;
		}
	}
	const code = bestFit >= DETECT_MIN_FIT ? bestCode : 'und';
	return { code, confidence: bestFit, script: census.dominant, fits };
}

/**
 * How a channel runs for a document language: the pack's own map when a pack is installed, else the
 * language-independent channels run ('agnostic', shown as "uncalibrated for ‹language›") and the rest
 * abstain ('unavailable', "not available for ‹language›").
 */
export function channelSupport(code: string, id: ChannelId): ChannelSupport {
	const pack = packs.get(code);
	if (pack) return pack.channels[id];
	return AGNOSTIC_CHANNELS.has(id) ? 'agnostic' : 'unavailable';
}
